import React, { useEffect, useState, useContext, useRef } from "react";
import {
  Container,
  Card,
  Table,
  Badge,
  Form,
  Row,
  Col,
  Alert,
  Spinner,
  InputGroup,
  Pagination,
  Button,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  getMoodleCacheCourses,
  checkMoodleLiveStatus,
  deleteMoodleCacheEntry,
  MoodleCacheCourse,
  MoodleCacheResponse,
  MoodleLiveStatusResult,
} from "../../services/api";
import { useErrorStore } from "../../errorStore";
import { UserContext } from "../../userContext";
import { Search, Trash } from "react-bootstrap-icons";

export default function MoodleCacheOverview() {
  const { t } = useTranslation("common");
  const [courses, setCourses] = useState<MoodleCacheCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [pagination, setPagination] = useState<MoodleCacheResponse["pagination"] | null>(null);
  const [liveStatus, setLiveStatus] = useState<{ [shortname: string]: MoodleLiveStatusResult }>({});
  const [liveLoading, setLiveLoading] = useState<Set<string>>(new Set());
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const setErrorMessage = useErrorStore((state) => state.setErrorMessage);
  const currentUser = useContext(UserContext);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadCourses = async (page: number = 1, search?: string) => {
    try {
      setLoading(true);
      const response = await getMoodleCacheCourses(page, perPage, search);
      setCourses(response.data);
      setPagination(response.pagination);
      setCurrentPage(page);

      // Trigger live status check for loaded courses
      if (response.data.length > 0) {
        checkLiveForCourses(response.data);
      }
    } catch (error: any) {
      setErrorMessage("Fehler beim Laden der Moodle-Cache-Daten");
      console.error("Error loading moodle cache:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkLiveForCourses = async (coursesToCheck: MoodleCacheCourse[]) => {
    const shortnames = coursesToCheck.map((c) => c.shortname).filter(Boolean);
    if (shortnames.length === 0) return;

    setLiveLoading(new Set(shortnames));

    try {
      const response = await checkMoodleLiveStatus(shortnames);
      setLiveStatus((prev) => ({ ...prev, ...response.results }));
    } catch (error: any) {
      console.error("Error checking live status:", error);
    } finally {
      setLiveLoading(new Set());
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(`Cache-Eintrag #${id} wirklich löschen?`)) return;

    try {
      setDeletingId(id);
      await deleteMoodleCacheEntry(id);
      setCourses((prev) => prev.filter((c) => c.id !== id));
      if (pagination) {
        setPagination({ ...pagination, total: pagination.total - 1 });
      }
    } catch (error: any) {
      setErrorMessage("Fehler beim Löschen des Cache-Eintrags");
      console.error("Error deleting cache entry:", error);
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    loadCourses(1, searchTerm);
  }, [perPage]);

  // Debounced search
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 400);
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, [searchInput]);

  useEffect(() => {
    loadCourses(1, searchTerm);
  }, [searchTerm]);

  const handlePageChange = (page: number) => {
    setLiveStatus({});
    loadCourses(page, searchTerm);
  };

  // Check admin access
  if (currentUser?.role !== "admin") {
    return (
      <Container>
        <Alert variant="danger">Zugriff verweigert</Alert>
      </Container>
    );
  }

  const stats = {
    total: pagination?.total || 0,
  };

  const renderLiveStatus = (shortname: string) => {
    if (liveLoading.has(shortname)) {
      return <Spinner animation="border" size="sm" />;
    }
    const status = liveStatus[shortname];
    if (!status) {
      return <span className="text-muted">-</span>;
    }
    if (status.exists) {
      return (
        <Badge bg="success" title={`Moodle ID: ${status.id}`}>
          Existiert
        </Badge>
      );
    }
    return <Badge bg="danger">Nicht gefunden</Badge>;
  };

  const renderEnrolledCount = (shortname: string) => {
    if (liveLoading.has(shortname)) {
      return <Spinner animation="border" size="sm" />;
    }
    const status = liveStatus[shortname];
    if (!status || !status.exists) {
      return <span className="text-muted">-</span>;
    }
    return <span>{status.teachers ?? 0}</span>;
  };

  const renderStudentCount = (shortname: string) => {
    if (liveLoading.has(shortname)) {
      return <Spinner animation="border" size="sm" />;
    }
    const status = liveStatus[shortname];
    if (!status || !status.exists) {
      return <span className="text-muted">-</span>;
    }
    return <span>{status.students ?? 0}</span>;
  };

  if (loading) {
    return (
      <Container>
        <div className="text-center mt-5">
          <Spinner animation="border" />
        </div>
      </Container>
    );
  }

  return (
    <Container fluid>
      <h2>Moodle Cache</h2>

      {/* Statistik-Karten */}
      <Row className="mb-4">
        <Col sm={6} md={4} lg={2}>
          <Card className="text-center">
            <Card.Body>
              <h3>{stats.total}</h3>
              <small className="text-muted">Gesamt im Cache</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filter */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Filter</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <Search />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Shortname, Fullname oder IDNumber suchen..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={2}>
              <Form.Select
                value={perPage}
                onChange={(e) => setPerPage(Number(e.target.value))}
              >
                <option value={5}>5 pro Seite</option>
                <option value={10}>10 pro Seite</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <div className="text-muted pt-2">
                {courses.length} Einträge angezeigt
                {pagination && ` (Seite ${pagination.current_page} von ${pagination.total_pages})`}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tabelle */}
      <Card className="mb-4">
        <Card.Body className="p-0">
          <Table striped hover responsive className="mb-0">
            <thead>
              <tr>
                <th>ID</th>
                <th>Fullname</th>
                <th>Shortname</th>
                <th>IDNumber</th>
                <th>Category</th>
                <th>Moodle Live</th>
                <th>Teachers</th>
                <th>Students</th>
                <th>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {courses.length > 0 ? (
                courses.map((course) => (
                  <tr key={course.id}>
                    <td>{course.id}</td>
                    <td>
                      <small>{course.fullname}</small>
                    </td>
                    <td>
                      <code>{course.shortname}</code>
                    </td>
                    <td>
                      <small>{course.idnumber || "-"}</small>
                    </td>
                    <td>{course.categoryid}</td>
                    <td>{renderLiveStatus(course.shortname)}</td>
                    <td>{renderEnrolledCount(course.shortname)}</td>
                    <td>{renderStudentCount(course.shortname)}</td>
                    <td>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(course.id)}
                        disabled={deletingId === course.id}
                      >
                        {deletingId === course.id ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <Trash />
                        )}
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="text-center text-muted py-4">
                    Keine Cache-Einträge gefunden
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Pagination */}
      {pagination && pagination.total_pages > 1 && (
        <div className="d-flex justify-content-center">
          <Pagination>
            <Pagination.First
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            />

            {(() => {
              const pages = [];
              const maxPages = 5;
              let startPage = Math.max(1, currentPage - Math.floor(maxPages / 2));
              let endPage = Math.min(pagination.total_pages, startPage + maxPages - 1);

              if (endPage - startPage < maxPages - 1) {
                startPage = Math.max(1, endPage - maxPages + 1);
              }

              if (startPage > 1) {
                pages.push(
                  <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                  </Pagination.Item>
                );
                if (startPage > 2) {
                  pages.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
                }
              }

              for (let i = startPage; i <= endPage; i++) {
                pages.push(
                  <Pagination.Item
                    key={i}
                    active={i === currentPage}
                    onClick={() => handlePageChange(i)}
                  >
                    {i}
                  </Pagination.Item>
                );
              }

              if (endPage < pagination.total_pages) {
                if (endPage < pagination.total_pages - 1) {
                  pages.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
                }
                pages.push(
                  <Pagination.Item
                    key={pagination.total_pages}
                    onClick={() => handlePageChange(pagination.total_pages)}
                  >
                    {pagination.total_pages}
                  </Pagination.Item>
                );
              }

              return pages;
            })()}

            <Pagination.Next
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === pagination.total_pages}
            />
            <Pagination.Last
              onClick={() => handlePageChange(pagination.total_pages)}
              disabled={currentPage === pagination.total_pages}
            />
          </Pagination>
        </div>
      )}
    </Container>
  );
}
